/**
 * @packageDocumentation
 * Implements the bottom HUD bar that tracks total win, bet, and balance values with responsive layout helpers.
 *
 * Assumptions:
 * - Landscape bottom bar design height: 180
 * - Portrait bottom bar design height: 240
 */

import * as PIXI from "pixi.js";
import { StartAppResize } from "./../resizer";
import { BottomcanvasProperties } from "../config";
import { Balance, currencySymbol } from "./server";
import { applyCent, font_secondary } from "./global";

export class BottomBar {
  // ✅ Static reference to the Pixi Application that owns the bottom bar
  static app: PIXI.Application;

  /** PIXI Application instance for bottom bar */
  public app_bottom: PIXI.Application = new PIXI.Application();

  /** Text display for total bet amount */
  static TotalBet: PIXI.Text;
  /** Text display for balance amount */
  static BalanceText: PIXI.Text;
  /** Text label for balance */
  static BalanceTextColon: PIXI.Text;
  /** Text label for total win */
  static TotalWinColon: PIXI.Text;
  /** Text label for total bet */
  static TotalBetColon: PIXI.Text;
  /** Text display for total win amount */
  static TotalWin: PIXI.Text;

  /** Current total win value */
  static TotalWinvalue: number = 0;
  /** Current total bet value */
  static TotalBetvalue: number = 0;
  /** Current balance value */
  static TotalBalancevalue: number = Balance;
  /** Current special win type (BIG WIN, MEGA WIN etc) */
  static currentSpecialWin = "";

  constructor() {
    (async () => {
      await this.app_bottom.init({
        width: BottomcanvasProperties.width,
        height: BottomcanvasProperties.height,
        backgroundColor: "black",
        backgroundAlpha: 0,
        resolution: 2,
      });

      // ✅ Set the static app reference ONCE so static layout methods can read screen size
      BottomBar.app = this.app_bottom;

      // Build the UI
      this.TotalBetfun();
      this.TotalWinFun();
      this.BalanceTextfun();

      // Initial align
      if (window.matchMedia("(orientation:portrait)").matches) {
        BottomBar.AlignPortrait();
      } else {
        BottomBar.AlignLandscape();
      }

      // Resize/position via shared resizer
      StartAppResize({
        app: this.app_bottom,
        app_width: BottomcanvasProperties.width,
        app_height: BottomcanvasProperties.height,
        resize_to_device: BottomcanvasProperties.resize,
        transformOrigin_Y: 100,
        translate_Y: 100,
        app_top: 100,
        app_name: "bottom_app",
      });

      // Canvas background art
      this.app_bottom.canvas.style.backgroundImage =
        "url('./assets/Images/bottomBarBG.png')";
      this.app_bottom.canvas.style.backgroundSize = "cover";
      this.app_bottom.canvas.style.backgroundPosition = "center";
      this.app_bottom.canvas.style.backgroundRepeat = "no-repeat";

      // OPTIONAL: Ensure canvas doesn't swallow clicks outside bar if CSS size is ever wrong.
      // (If your canvas is sized correctly by resizer, this is not strictly needed.)
      // this.app_bottom.canvas.style.pointerEvents = "auto";
    })();
  }

  // -----------------------
  // Styles
  // -----------------------
  private makeLabelStyle(fontSize: number) {
    return new PIXI.TextStyle({
      fontFamily: font_secondary,
      dropShadow: {
        alpha: 0.8,
        angle: 1,
        blur: 4,
        color: "0x111111",
        distance: 2,
      },
      align: "center",
      fill: "#fdd875",
      fontSize,
      fontWeight: "bold",
    });
  }

  private makeValueStyle(fontSize: number) {
    return new PIXI.TextStyle({
      fontFamily: font_secondary,
      dropShadow: {
        alpha: 0.8,
        angle: 1,
        blur: 4,
        color: "0x111111",
        distance: 2,
      },
      align: "center",
      fill: "0xffffff",
      fontSize,
      fontWeight: "bold",
    });
  }

  // -----------------------
  // Element creation
  // -----------------------
  BalanceTextfun() {
    BottomBar.BalanceTextColon = new PIXI.Text({
      text: "BALANCE: ",
      style: this.makeLabelStyle(40),
    });
    BottomBar.BalanceTextColon.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.BalanceTextColon);

    BottomBar.BalanceText = new PIXI.Text({
      text: currencySymbol + Balance.toFixed(2),
      style: this.makeValueStyle(40),
    });
    BottomBar.BalanceText.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.BalanceText);
  }

  TotalWinFun() {
    BottomBar.TotalWinColon = new PIXI.Text({
      text: "TOTAL WIN: ",
      style: this.makeLabelStyle(40),
    });
    BottomBar.TotalWinColon.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.TotalWinColon);

    BottomBar.TotalWin = new PIXI.Text({
      text: currencySymbol + "0.00",
      style: this.makeValueStyle(40),
    });
    BottomBar.TotalWin.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.TotalWin);
  }

  TotalBetfun() {
    BottomBar.TotalBetColon = new PIXI.Text({
      text: "TOTAL BET: ",
      style: this.makeLabelStyle(40),
    });
    BottomBar.TotalBetColon.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.TotalBetColon);

    BottomBar.TotalBet = new PIXI.Text({
      text: `${currencySymbol}10.00`,
      style: this.makeValueStyle(40),
    });
    BottomBar.TotalBet.anchor.set(0, 0.5);
    this.app_bottom.stage.addChild(BottomBar.TotalBet);
  }

  // -----------------------
  // Updates
  // -----------------------
  static updateTotalBet(value: number) {
    BottomBar.TotalBetvalue = value;
    BottomBar.TotalBet.text = `${currencySymbol}${value.toFixed(2)}`;
    applyCent(BottomBar.TotalBet, value);
    BottomBar.realign();
  }

  static updateTotalWin(value: number) {
    BottomBar.currentSpecialWin = "WON";
    BottomBar.TotalWinvalue = value;
    BottomBar.TotalWin.text = `${currencySymbol}${value.toFixed(2)}`;
    applyCent(BottomBar.TotalWin, value);
    BottomBar.realign();
  }

  static updateBalance(value: number) {
    BottomBar.TotalBalancevalue = value;
    BottomBar.BalanceText.text = `${currencySymbol}${value.toFixed(2)}`;
    applyCent(BottomBar.BalanceText, value);
    BottomBar.realign();
  }

  private static realign() {
    if (!BottomBar.app) return;
    if (BottomBar.isPortraitNow()) BottomBar.AlignPortrait();
    else BottomBar.AlignLandscape();
  }

  // -----------------------
  // Layout helpers
  // -----------------------
  private static getWH() {
    const W = BottomBar.app.renderer.screen.width;
    const H = BottomBar.app.renderer.screen.height;
    return { W, H };
  }

  private static isPortraitNow() {
    const { W, H } = BottomBar.getWH();
    return H > W;
  }

  /**
   * Aligns UI elements for portrait bottom bar.
   * Expected design size (from resizer): 1080 x 240
   */
  /**
 * Aligns UI elements for portrait bottom bar.
 * Expected design size (from resizer): 1080 x 140
 */
  static AlignPortrait() {
    const { W, H } = BottomBar.getWH();

    // -----------------------
    // TUNE THESE VALUES (portrait bar)
    // -----------------------
    const FONT = 44;
    const LEFT_PAD = 24;
    const RIGHT_PAD = 24;

    // Define 2-row layout inside the bar height
    const TOP_ROW_Y = Math.round(H * 0.35);
    const BOTTOM_ROW_Y = Math.round(H * 0.78);

    // Apply font sizes
    BottomBar.TotalWinColon.style.fontSize = FONT;
    BottomBar.TotalWin.style.fontSize = FONT;
    BottomBar.BalanceTextColon.style.fontSize = FONT;
    BottomBar.BalanceText.style.fontSize = FONT;
    BottomBar.TotalBetColon.style.fontSize = FONT;
    BottomBar.TotalBet.style.fontSize = FONT;

    // Row 1 (top): TOTAL WIN centered
    BottomBar.TotalWinColon.y = TOP_ROW_Y;
    BottomBar.TotalWin.y = TOP_ROW_Y;

    BottomBar.TotalWinColon.x =
      W / 2 -
      BottomBar.TotalWinColon.width / 2 -
      BottomBar.TotalWin.width / 2;

    BottomBar.TotalWin.x =
      BottomBar.TotalWinColon.x + BottomBar.TotalWinColon.width;

    // Row 2 (bottom-left): BALANCE
    BottomBar.BalanceTextColon.x = LEFT_PAD;
    BottomBar.BalanceTextColon.y = BOTTOM_ROW_Y;
    BottomBar.BalanceText.x =
      BottomBar.BalanceTextColon.x + BottomBar.BalanceTextColon.width;
    BottomBar.BalanceText.y = BOTTOM_ROW_Y;

    // Row 2 (bottom-right): TOTAL BET aligned to right
    BottomBar.TotalBetColon.y = BOTTOM_ROW_Y;
    BottomBar.TotalBet.y = BOTTOM_ROW_Y;

    BottomBar.TotalBetColon.x =
      W - RIGHT_PAD - (BottomBar.TotalBetColon.width + BottomBar.TotalBet.width);

    BottomBar.TotalBet.x =
      BottomBar.TotalBetColon.x + BottomBar.TotalBetColon.width;
  }


  /**
   * Aligns UI elements for landscape bottom bar.
   * Expected design size (from resizer): 1920 x 180
   */
  /**
 * Aligns UI elements for landscape bottom bar.
 * Expected design size (from resizer): 1920 x 90
 */
  static AlignLandscape() {
    const { W, H } = BottomBar.getWH();

    // -----------------------
    // TUNE THESE VALUES (landscape bar)
    // -----------------------
    const FONT = 22;
    const LEFT_X = 60;
    const MID_X_OFFSET = 60;

    const Y = Math.round(H / 2);

    // Apply font sizes
    BottomBar.TotalWinColon.style.fontSize = FONT;
    BottomBar.TotalWin.style.fontSize = FONT;
    BottomBar.BalanceTextColon.style.fontSize = FONT;
    BottomBar.BalanceText.style.fontSize = FONT;
    BottomBar.TotalBetColon.style.fontSize = FONT;
    BottomBar.TotalBet.style.fontSize = FONT;

    // LEFT group: BALANCE
    BottomBar.BalanceTextColon.x = LEFT_X;
    BottomBar.BalanceTextColon.y = Y;
    BottomBar.BalanceText.x =
      BottomBar.BalanceTextColon.x + BottomBar.BalanceTextColon.width;
    BottomBar.BalanceText.y = Y;

    // MIDDLE group: TOTAL WIN
    BottomBar.TotalWinColon.x = (W / 3) * 1 + MID_X_OFFSET;
    BottomBar.TotalWinColon.y = Y;
    BottomBar.TotalWin.x =
      BottomBar.TotalWinColon.x + BottomBar.TotalWinColon.width;
    BottomBar.TotalWin.y = Y;

    // RIGHT group: TOTAL BET
    BottomBar.TotalBetColon.x = (W / 3) * 2 + MID_X_OFFSET;
    BottomBar.TotalBetColon.y = Y;
    BottomBar.TotalBet.x =
      BottomBar.TotalBetColon.x + BottomBar.TotalBetColon.width;
    BottomBar.TotalBet.y = Y;
  }

}
